// src/hooks/useEquipment.ts
import { useCallback, useEffect, useState } from 'react';
import api from '@/services/api';

export function useEquipment(id?: string) {
  const [equipment, setEquipment] = useState<EquipmentType | null>(null);
  const [params, setParams] = useState<ParameterType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Sorts WANs by connection type priority: PPPOE > DHCP > STATIC
  const sortWans = (wans?: NetworkInterfaceType[]) => {
    if (!wans || !Array.isArray(wans)) return;
    wans.sort((a, b) => {
      const priority: Record<string, number> = { PPPOE: 1, DHCP: 2, STATIC: 3 };
      const typeA = String(a?.connectionType?.value || '').toUpperCase();
      const typeB = String(b?.connectionType?.value || '').toUpperCase();
      return (priority[typeA] || 99) - (priority[typeB] || 99);
    });
  };

  // Sorts a flat list of Wi-Fi APs by status priority: Active > Disabled > Unknown
  const sortWifis = (wifis?: NetworkInterfaceType[]) => {
    if (!wifis || !Array.isArray(wifis)) return;
    const getApPriority = (wifi: NetworkInterfaceType): number => {
      const ssidEnable = wifi?.ssidEnable;
      const visible = wifi?.visible;
      // Unknown: fields not returned / null
      if (ssidEnable == null || visible == null) return 2;
      const isActive = String(ssidEnable.value) === 'true';
      const isVisible = String(visible.value) === 'true';
      if (isActive && isVisible) return 0; // Active
      if (!isVisible) return 1; // Disabled
      return 2; // Unkonwn / other cases
    };
    // Stable sort: annotate with original index so unknown APs keep backend order
    const annotated = wifis.map((wifi, i) => ({
      wifi,
      originalIndex: i,
      priority: getApPriority(wifi),
    }));
    annotated.sort((a, b) =>
      a.priority !== b.priority
        ? a.priority - b.priority
        : a.originalIndex - b.originalIndex
    );
    annotated.forEach(({ wifi }, i) => {
      wifis[i] = wifi;
    });
  };

  // Get equipment data by ID when fetchEquipment is called
  const fetchEquipment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/devices/${id}`);
      sortWans(data?.wans);
      sortWifis(data?.wifis);
      setEquipment(data);
      setParams(
        (data?.values ?? []).filter((v: ParameterType | null) => v != null)
      );
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Calls fetchEquipment when component mounts or id changes
  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  // Syncronize equipment data when refetchEquipment is called
  const refetchEquipment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // Gets equipment syncronized data from api
      const { data } = await api.get(`/devices/${id}/synchronize`);
      sortWans(data?.wans);
      sortWifis(data?.wifis);

      setEquipment((prev) => ({ ...prev, ...data }));
      setParams([
        ...(data?.values ?? []).filter((v: ParameterType | null) => v != null),
      ]);
      setError(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Synchronize equipment ERP data
  const syncErpEquipment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/devices/${id}/synchronize-erp`);
      sortWans(data?.wans);
      sortWifis(data?.wifis);

      setEquipment((prev) => ({ ...prev, ...data }));
      setParams([
        ...(data?.values ?? []).filter((v: ParameterType | null) => v != null),
      ]);
      setError(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  return {
    equipment,
    setEquipment,
    params,
    loading,
    setLoading,
    error,
    refetch: fetchEquipment,
    refetchEquipment: refetchEquipment,
    syncErpEquipment,
  };
}
